import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockDatacenters } from './mockData';
import type { Datacenter } from './types';
import DatacenterDetailModal from './components/DatacenterDetailModal';
import DatacenterFormModal from './components/DatacenterFormModal';

export default function DatacentersPage() {
  const [datacenters, setDatacenters] = useState<Datacenter[]>(mockDatacenters);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'normal' | 'maintenance' | 'retired'>('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [selectedDatacenter, setSelectedDatacenter] = useState<Datacenter | null>(null);
  const [editingDatacenter, setEditingDatacenter] = useState<Datacenter | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const cities = Array.from(new Set(mockDatacenters.map(dc => dc.location.city)));

  const filteredDatacenters = datacenters.filter(dc => {
    if (searchText && !(
      dc.name.toLowerCase().includes(searchText.toLowerCase()) ||
      dc.id.toLowerCase().includes(searchText.toLowerCase()) ||
      dc.code.toLowerCase().includes(searchText.toLowerCase())
    )) return false;
    if (statusFilter !== 'all' && dc.status !== statusFilter) return false;
    if (cityFilter !== 'all' && dc.location.city !== cityFilter) return false;
    return true;
  });

  const totalStats = datacenters.reduce((acc, dc) => ({
    datacenters: acc.datacenters + 1,
    servers: acc.servers + dc.resources.serverCount,
    instances: acc.instances + dc.resources.instanceStats.total,
    cpu: acc.cpu + dc.resources.totalCpu,
    memory: acc.memory + dc.resources.totalMemory,
    disk: acc.disk + dc.resources.totalDisk,
    t4: acc.t4 + dc.resources.gpuStats.t4,
    rtx5070: acc.rtx5070 + dc.resources.gpuStats.rtx5070,
    rtx5090_16g: acc.rtx5090_16g + dc.resources.gpuStats.rtx5090_16g,
    rtx5090_32g: acc.rtx5090_32g + dc.resources.gpuStats.rtx5090_32g,
  }), {
    datacenters: 0,
    servers: 0,
    instances: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    t4: 0,
    rtx5070: 0,
    rtx5090_16g: 0,
    rtx5090_32g: 0,
  });

  const handleSaveDatacenter = (datacenterData: Partial<Datacenter>) => {
    if (editingDatacenter) {
      // 编辑现有机房
      setDatacenters(datacenters.map(dc => 
        dc.id === editingDatacenter.id ? { ...dc, ...datacenterData } : dc
      ));
      setEditingDatacenter(null);
    } else {
      // 创建新机房
      const newDatacenter: Datacenter = {
        id: `DC-${String(datacenters.length + 1).padStart(3, '0')}`,
        ...datacenterData,
        status: 'normal',
        resources: {
          serverCount: 0,
          totalCpu: 0,
          totalMemory: 0,
          totalDisk: 0,
          gpuStats: { t4: 0, rtx5070: 0, rtx5090_16g: 0, rtx5090_32g: 0 },
          instanceStats: { online: 0, total: 0 },
        },
        monitoring: {
          bandwidth: { used: 0, total: 10 },
          instanceOnlineRate: { rate: 0, online: 0, total: 0 },
          cpuUsage: { rate: 0, used: 0, total: 0 },
          gpuUsage: { rate: 0, used: 0, total: 0 },
          memoryUsage: { rate: 0, used: 0, total: 0 },
          diskUsage: { rate: 0, used: 0, total: 0 },
        },
      } as Datacenter;
      setDatacenters([...datacenters, newDatacenter]);
      setShowCreateModal(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">机房管理</h1>
              <p className="text-sm text-gray-500 mt-1">管理云电脑服务的数据中心和机房信息</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>
              新建机房
            </button>
          </div>
        </div>

        {/* 机房概览 */}
        <div className="mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">机房概览</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">机房总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.datacenters}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">服务器总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.servers}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">实例总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.instances}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">CPU 总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.cpu}核</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">内存总容量</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.memory}GB</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">磁盘总容量</div>
              <div className="text-2xl font-semibold text-gray-900">{(totalStats.disk / 1024).toFixed(1)}TB</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">T4 总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.t4}张</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">5070 总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.rtx5070}张</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">5090(16G) 总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.rtx5090_16g}张</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-1">5090(32G) 总数</div>
              <div className="text-2xl font-semibold text-gray-900">{totalStats.rtx5090_32g}张</div>
            </div>
          </div>
        </div>

        {/* 机房列表 */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">搜索</label>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="机房名称、ID、代码"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">机房状态</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部</option>
                  <option value="normal">正常</option>
                  <option value="maintenance">维护中</option>
                  <option value="retired">已退场</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">所属城市</label>
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部城市</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">机房ID</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">机房名称+代码</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">机房位置</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">服务器数量</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">总资源统计</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">实例总数</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">机房状态</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredDatacenters.map((dc) => (
                  <tr key={dc.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{dc.id}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <div className="font-medium">{dc.name}</div>
                      <div className="text-xs text-gray-500">{dc.code}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      {dc.location.province} {dc.location.city} {dc.location.district}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{dc.resources.serverCount}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <div>{dc.resources.totalCpu}核 / {dc.resources.totalMemory}GB</div>
                      <div className="text-xs text-gray-500">{(dc.resources.totalDisk / 1024).toFixed(1)}TB / {dc.resources.gpuStats.t4 + dc.resources.gpuStats.rtx5070 + dc.resources.gpuStats.rtx5090_16g + dc.resources.gpuStats.rtx5090_32g}张显卡</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      <span className="text-green-600 font-medium">{dc.resources.instanceStats.online}</span> / {dc.resources.instanceStats.total}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        dc.status === 'normal' ? 'bg-green-100 text-green-800' :
                        dc.status === 'maintenance' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {dc.status === 'normal' ? '正常' : dc.status === 'maintenance' ? '维护中' : '已退场'}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingDatacenter(dc)}
                          className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                          title="编辑"
                        >
                          <i className="ri-edit-line text-base"></i>
                        </button>
                        <button
                          onClick={() => setSelectedDatacenter(dc)}
                          className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                          title="查看详情"
                        >
                          <i className="ri-eye-line text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedDatacenter && (
        <DatacenterDetailModal
          datacenter={selectedDatacenter}
          onClose={() => setSelectedDatacenter(null)}
          onEdit={() => {
            setEditingDatacenter(selectedDatacenter);
            setSelectedDatacenter(null);
          }}
        />
      )}

      {(showCreateModal || editingDatacenter) && (
        <DatacenterFormModal
          datacenter={editingDatacenter}
          onClose={() => {
            setShowCreateModal(false);
            setEditingDatacenter(null);
          }}
          onSave={handleSaveDatacenter}
        />
      )}
    </MainLayout>
  );
}
